import { jsx, jsxs } from "react/jsx-runtime";
import React, { useState } from "react";
import { DateInput, DateRangeInput } from "@blueprintjs/datetime";
import { camelCase, startCase } from "lodash-es";
import classNames from "classnames";
import {
  Menu,
  Intent,
  MenuDivider,
  InputGroup,
  Classes,
  NumericInput,
  MenuItem
} from "@blueprintjs/core";
import dayjs from "dayjs";
import getDayjsFormatter from "../utils/getDayjsFormatter";
import { onEnterHelper } from "../utils/handlerHelpers";
import DialogFooter from "../DialogFooter";
import TgSelect from "../TgSelect";
import "@teselagen/react-table/react-table.css";
import "./style.css";
import "../toastr";
const filterTypesDictionary = {
  none: "",
  startsWith: "text",
  endsWith: "text",
  contains: "text",
  notContains: "text",
  isExactly: "text",
  isEmpty: "text",
  notEmpty: "text",
  inList: "list",
  notInList: "list",
  true: "boolean",
  false: "boolean",
  dateIs: "date",
  notBetween: "dateRange",
  isBetween: "dateRange",
  isBefore: "date",
  isAfter: "date",
  greaterThan: "number",
  lessThan: "number",
  inRange: "numberRange",
  outsideRange: "numberRange",
  equalTo: "number",
  regex: "text"
};
const isInvalidFilterValue = (value) => {
  if (Array.isArray(value) && value.length) {
    return value.some((item) => isInvalidFilterValue(item));
  }
  return value === "" || value === void 0 || value.length === 0;
};
const FilterAndSortMenu = ({
  dataType,
  togglePopover,
  filterOn,
  addFilters,
  removeSingleFilter,
  currentFilter
}) => {
  const [selectedFilter, setSelectedFilter] = useState(
    currentFilter?.selectedFilter ?? camelCase(getFilterMenuItems(dataType)[0])
  );
  const [filterValue, setFilterValue] = useState(
    currentFilter?.filterValue ?? ""
  );
  const handleFilterChange = (selectedFilter2) => {
    if (filterValue && !Array.isArray(filterValue) && filterTypesDictionary[selectedFilter2] === "list") {
      setFilterValue(filterValue?.split(" ") || []);
    } else if (filterTypesDictionary[selectedFilter2] === "text" && Array.isArray(filterValue)) {
      setFilterValue(filterValue.join(" "));
    }
    setSelectedFilter(camelCase(selectedFilter2));
  };
  const handleFilterValueChange = (filterValue2) => setFilterValue(filterValue2);
  const handleFilterSubmit = () => {
    const ccSelectedFilter2 = camelCase(selectedFilter);
    let filterValToUse = filterValue;
    if (ccSelectedFilter2 === "true" || ccSelectedFilter2 === "false") {
      filterValToUse = ccSelectedFilter2;
    } else if (ccSelectedFilter2 === "notEmpty") {
      filterValToUse = true;
    } else if (ccSelectedFilter2 === "isEmpty") {
      filterValToUse = false;
    } else if (ccSelectedFilter2 === "inList" || ccSelectedFilter2 === "notInList") {
      if (dataType === "number") {
        filterValToUse = filterValue && filterValue.map((val) => parseFloat(val.replaceAll(",", "")));
      }
    }
    if (isInvalidFilterValue(filterValToUse)) {
      togglePopover();
      return removeSingleFilter(filterOn);
    }
    addFilters([
      {
        filterOn,
        selectedFilter: ccSelectedFilter2,
        filterValue: filterValToUse
      }
    ]);
    togglePopover();
  };
  const filterMenuItems = getFilterMenuItems(dataType);
  const ccSelectedFilter = camelCase(selectedFilter);
  const requiresValue = ccSelectedFilter && ccSelectedFilter !== "none";
  return /* @__PURE__ */ jsxs(Menu, { className: "data-table-header-menu", children: [
    /* @__PURE__ */ jsx("div", { className: "custom-menu-item", children: /* @__PURE__ */ jsx("div", { className: classNames(Classes.SELECT, Classes.FILL), children: /* @__PURE__ */ jsx(
      "select",
      {
        onChange: function(e) {
          const ccSelectedFilter2 = camelCase(e.target.value);
          handleFilterChange(ccSelectedFilter2);
        },
        value: ccSelectedFilter,
        children: filterMenuItems.map(function(menuItem, index) {
          return /* @__PURE__ */ jsx("option", { value: camelCase(menuItem), children: menuItem }, index);
        })
      }
    ) }) }),
    /* @__PURE__ */ jsx("div", { className: "custom-menu-item", children: /* @__PURE__ */ jsx(
      FilterInput,
      {
        dataType,
        requiresValue,
        handleFilterSubmit,
        filterValue,
        handleFilterValueChange,
        filterSubType: camelCase(selectedFilter),
        filterType: filterTypesDictionary[camelCase(selectedFilter)]
      }
    ) }),
    /* @__PURE__ */ jsx(MenuDivider, {}),
    /* @__PURE__ */ jsx(
      DialogFooter,
      {
        secondaryClassName: Classes.POPOVER_DISMISS,
        onClick: () => {
          handleFilterSubmit();
        },
        intent: Intent.SUCCESS,
        text: "Filter",
        secondaryText: "Clear",
        secondaryIntent: Intent.DANGER,
        secondaryAction: () => {
          if (currentFilter) removeSingleFilter(currentFilter.filterOn);
        }
      }
    )
  ] });
};
export default FilterAndSortMenu;
const dateMinMaxHelpers = {
  minDate: dayjs().subtract(25, "years").toDate(),
  maxDate: dayjs().add(25, "years").toDate()
};
const renderCreateNewOption = (query, active, handleClick) => /* @__PURE__ */ jsx(
  MenuItem,
  {
    icon: "add",
    text: query,
    active,
    onClick: handleClick,
    shouldDismissPopover: false
  }
);
const FilterInput = ({
  handleFilterValueChange,
  handleFilterSubmit,
  filterValue,
  filterSubType,
  filterType
}) => {
  let inputGroup = /* @__PURE__ */ jsx("div", {});
  switch (filterType) {
    case "text":
      inputGroup = filterSubType === "notEmpty" || filterSubType === "isEmpty" ? /* @__PURE__ */ jsx("div", {}) : /* @__PURE__ */ jsx("div", { className: "custom-menu-item", children: /* @__PURE__ */ jsx(
        InputGroup,
        {
          placeholder: "Value",
          onChange: function(e) {
            handleFilterValueChange(e.target.value);
          },
          autoFocus: true,
          ...onEnterHelper(handleFilterSubmit),
          value: filterValue
        }
      ) });
      break;
    case "list":
      inputGroup = /* @__PURE__ */ jsx("div", { className: "custom-menu-item", children: /* @__PURE__ */ jsx(
        TgSelect,
        {
          placeholder: "Add item",
          renderCreateNewOption,
          noResults: null,
          multi: true,
          creatable: true,
          value: (filterValue || []).map((val) => ({
            label: val,
            value: val
          })),
          onChange: (selectedOptions) => {
            selectedOptions.some((opt) => opt.value === "") ? handleFilterSubmit() : handleFilterValueChange(
              selectedOptions.map((opt) => opt.value)
            );
          },
          options: []
        }
      ) });
      break;
    case "number":
      inputGroup = /* @__PURE__ */ jsx("div", { className: "custom-menu-item", children: /* @__PURE__ */ jsx(
        NumericInput,
        {
          placeholder: "Value",
          onValueChange: function(numVal) {
            handleFilterValueChange(isNaN(numVal) ? 0 : numVal);
          },
          autoFocus: true,
          ...onEnterHelper(handleFilterSubmit),
          value: filterValue
        }
      ) });
      break;
    case "numberRange":
      inputGroup = /* @__PURE__ */ jsxs("div", { className: "custom-menu-item", children: [
        /* @__PURE__ */ jsx(
          NumericInput,
          {
            placeholder: "Low",
            onValueChange: function(numVal) {
              handleFilterValueChange([
                isNaN(numVal) ? 0 : numVal,
                filterValue[1]
              ]);
            },
            ...onEnterHelper(handleFilterSubmit),
            value: filterValue && filterValue[0]
          }
        ),
        /* @__PURE__ */ jsx(
          NumericInput,
          {
            placeholder: "High",
            onValueChange: function(numVal) {
              handleFilterValueChange([
                filterValue[0],
                isNaN(numVal) ? 0 : numVal
              ]);
            },
            ...onEnterHelper(handleFilterSubmit),
            value: filterValue && filterValue[1]
          }
        )
      ] });
      break;
    case "date":
      inputGroup = /* @__PURE__ */ jsx("div", { className: "custom-menu-item", children: /* @__PURE__ */ jsx(
        DateInput,
        {
          value: filterValue ? dayjs(filterValue).toDate() : void 0,
          ...getDayjsFormatter("L"),
          ...dateMinMaxHelpers,
          onChange: (selectedDates) => {
            handleFilterValueChange(selectedDates);
          }
        }
      ) });
      break;
    case "dateRange":
      let filterValueToUse;
      if (Array.isArray(filterValue)) {
        filterValueToUse = filterValue;
      } else {
        filterValueToUse = filterValue && filterValue.split && filterValue.split(";");
      }
      inputGroup = /* @__PURE__ */ jsx("div", { className: "custom-menu-item", children: /* @__PURE__ */ jsx(
        DateRangeInput,
        {
          value: filterValueToUse && filterValueToUse[0] && filterValueToUse[1] ? [new Date(filterValueToUse[0]), new Date(filterValueToUse[1])] : void 0,
          popoverProps: {
            captureDismiss: true
          },
          formatDate: (date) => date == null ? "" : date.toLocaleDateString(),
          parseDate: (str) => new Date(Date.parse(str)),
          placeholder: "JS Date",
          ...dateMinMaxHelpers,
          onChange: (selectedDates) => {
            if (selectedDates[0] && selectedDates[1]) {
              handleFilterValueChange(selectedDates);
            }
          }
        }
      ) });
      break;
    default:
  }
  return inputGroup;
};
function getFilterMenuItems(dataType) {
  let filterMenuItems = [];
  if (dataType === "string") {
    filterMenuItems = [
      "contains",
      "notContains",
      "startsWith",
      "endsWith",
      "isExactly",
      "regex",
      "inList",
      "notInList",
      "isEmpty",
      "notEmpty"
    ];
  } else if (dataType === "lookup") {
    filterMenuItems = [
      "contains",
      "notContains",
      "startsWith",
      "endsWith",
      "isExactly",
      "regex"
    ];
  } else if (dataType === "boolean") {
    filterMenuItems = ["true", "false"];
  } else if (dataType === "number" || dataType === "integer") {
    filterMenuItems = [
      "greaterThan",
      "lessThan",
      "inRange",
      "outsideRange",
      "equalTo",
      "inList",
      "notInList"
    ];
  } else if (dataType === "timestamp") {
    filterMenuItems = ["isBetween", "notBetween", "isBefore", "isAfter"];
  }
  return filterMenuItems.map((item) => startCase(item));
}
